import React, { useCallback, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { continueSessionStreaming } from "services/AgentChatService";
import {
  createChatSession,
  fireNamingAgent,
  getAgentTypeFromEnv,
  loadScenarioJobs,
  normalizeWorkflowTagsForSend,
  useAutoSend,
} from "modules/chat/utils/chatUtils";
import { getAnalyst } from "services/AnalystService";
import { ArtifactKind, ArtifactMap } from "modules/artifacts/utils/artifactUtils";
import { ArtifactProto, ChatMessageProto, ChatResponse } from "build-ts/chat_session";
import { ChatMessageType, chatMessageTypeFromJSON, ExecutionStatusType } from "build-ts/common";
import { Header } from "build-ts/metadata";
import { ScheduledJobProto } from "build-ts/schedule";
import { extractScenarioJobsSavedByTools } from "utils/helperFuncUtils";
import showToast from "hooks/useCustomToast";
import { useChatContext } from "context/ChatContext";
import { useUIContext } from "context/UIContext";
import { ArtifactOpenOpts, ChatMessage } from "../../interfaces";
import { useChatStreaming } from "../hooks/useChatStreaming";
import { v4 as uuidv4 } from "uuid";
import ChatPanel from "../components/ChatPanel";

export interface ChatContainerProps {
  userId?: string;
  isLoadingChat: boolean;
  chatId: string | undefined;
  setChatId: React.Dispatch<React.SetStateAction<string | undefined>>;
  chatMessages: ChatMessage[];
  setChatMessages: React.Dispatch<React.SetStateAction<ChatMessage[]>>;
  sessionId?: string;
  onArtifactClick?: (artifact: ArtifactProto, opts?: ArtifactOpenOpts) => void;
  selectedAnalyst?: Header | null;
  cleanContent?: (content: string) => string;
  analystsList?: Header[];
  isLoadingAnalysts?: boolean;
  isFromEva?: boolean;
  onAnalystChange?: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  isArtifactsPanelVisible?: boolean;
  /** Registers one or more artifacts into the store on each streaming event. */
  registerArtifacts?: (...artifacts: ArtifactProto[]) => void;
  /** Resolves any artifact/sub-resource name to its openable ArtifactProto; forwarded to MarkdownRenderer. */
  lookupByName?: (name: string, kind?: ArtifactKind) => ArtifactProto | undefined;
  /** Full session artifact registry — forwarded to ChatPanel for the @ mention menu. */
  artifactMap?: ArtifactMap;
  isChatSaved?: boolean;
  initialPrompt?: string;
  /** When set, auto-send waits until selectedAnalyst.id matches before firing */
  initialAnalystId?: string;
  scenarioJobs?: ScheduledJobProto[];
  setScenarioJobs?: React.Dispatch<React.SetStateAction<ScheduledJobProto[]>>;
  setScenarioJobsSavedByTools?: React.Dispatch<React.SetStateAction<ScheduledJobProto[]>>;
}

const ChatContainer: React.FC<ChatContainerProps> = props => {
  const {
    userId = "",
    chatId,
    setChatId,
    setChatMessages,
    isFromEva = false,
    isChatSaved,
    cleanContent,
    registerArtifacts,
    setScenarioJobs,
    setScenarioJobsSavedByTools,
    initialPrompt,
    initialAnalystId,
    chatMessages,
    selectedAnalyst,
  } = props;

  const navigate = useNavigate();
  const location = useLocation();
  const { currentSessionHeader, setCurrentSessionHeader, setAnalystProto } = useChatContext();
  const { setIsNavbarCollapsed } = useUIContext();

  const {
    isLoading,
    setIsLoading,
    streamingStatusLabel,
    setStreamingStatusLabel,
    prepareForNewTurn,
    finalizeStreamingTurn,
    appendStreamingToken,
    handleExecutionStep,
    handleReasoningToken,
    applyStreamChunk,
  } = useChatStreaming({
    setChatMessages,
    registerArtifacts,
    setScenarioJobsSavedByTools,
    onArtifactClick: props.onArtifactClick,
  });

  // Core send handler
  const handleSendMessage = useCallback(
    async (
      inputMsg: string,
      newChat: boolean,
      analyst: Header | null | undefined,
      csvFiles?: { file: File; fileName: string }[]
    ) => {
      const msgToSend = normalizeWorkflowTagsForSend(inputMsg.trim());
      if ((!msgToSend && (!csvFiles || csvFiles.length === 0)) || (!chatId && !newChat)) return;
      setIsNavbarCollapsed(true);

      const userMsg = {
        id: uuidv4(),
        content: inputMsg.trim(),
        author: "user" as const,
      };
      const loadingMsg = { id: uuidv4(), content: "", author: "ai" as const };

      // Reset all streaming and reasoning state for the new turn.
      prepareForNewTurn();

      setChatMessages(prev => [...prev, userMsg, loadingMsg]);
      setIsLoading(true);

      // Read all uploaded file bytes once up-front so both new-chat and continue paths can use them.
      let uploadedFiles: { fileBytes: Uint8Array; fileName: string }[] | undefined;
      if (csvFiles && csvFiles.length > 0) {
        uploadedFiles = await Promise.all(
          csvFiles.map(async f => ({
            fileBytes: new Uint8Array(await f.file.arrayBuffer()),
            fileName: f.fileName,
          }))
        );
      }

      try {
        let response: ChatResponse | undefined;
        let wasStreamed = false;

        if (newChat) {
          if (!analyst) {
            showToast("Please select an analyst first", "", "warning");
            return;
          }
          const analystDetails = await getAnalyst(analyst.id!);
          if (!analystDetails) {
            showToast("Failed to load analyst details", "", "error");
            return;
          }
          setAnalystProto(analystDetails);

          const jobsForNewChat: ScheduledJobProto[] = await loadScenarioJobs(analystDetails?.header?.id);
          if (jobsForNewChat.length > 0) {
            setScenarioJobs?.(jobsForNewChat);
          }

          const agentTypeFromEnv = getAgentTypeFromEnv();

          // is_save mirrors the continueSessionStreaming flag: Eva unsaved chats
          // must NOT be persisted to DB on init (fixes is_save hardcoded-true bug).
          const newSessionId = await createChatSession(
            userId,
            analystDetails,
            jobsForNewChat,
            agentTypeFromEnv,
            isFromEva ? !!isChatSaved : true
          );
          if (!newSessionId) {
            showToast("Failed to create new chat session", "", "error");
            return;
          }

          setChatId(newSessionId);
          setCurrentSessionHeader({ id: newSessionId, name: "", author_id: userId });
          navigate(`/chat/${newSessionId}`, { replace: true });

          // Fire naming agent in the background.
          // Build a naming message that includes the user text (if any) plus one line
          // per attached file so the naming agent has full context even on file-only sends.
          const fileLines = (csvFiles ?? []).map(f => `[user added a file: ${f.fileName}]`).join("\n");
          const namingMsg = [msgToSend, fileLines].filter(Boolean).join("\n\n");
          if (namingMsg) {
            fireNamingAgent(namingMsg, newSessionId, userId, title => {
              setCurrentSessionHeader(prev => (prev ? { ...prev, name: title } : prev));
            });
          }

          wasStreamed = true;
          await continueSessionStreaming(
            newSessionId,
            msgToSend,
            isFromEva ? isChatSaved : true,
            userId,
            appendStreamingToken,
            chunk => {
              applyStreamChunk(chunk, value => {
                response = value;
              });
            },
            handleExecutionStep,
            handleReasoningToken,
            uploadedFiles
          );
          finalizeStreamingTurn();
        } else {
          // Continue existing chat
          wasStreamed = true;
          await continueSessionStreaming(
            chatId!,
            msgToSend,
            isFromEva ? isChatSaved : true,
            userId,
            appendStreamingToken,
            chunk => {
              applyStreamChunk(chunk, value => {
                response = value;
              });
            },
            handleExecutionStep,
            handleReasoningToken,
            uploadedFiles
          );
          finalizeStreamingTurn();
        }

        if (response?.status === ExecutionStatusType.TOOL_CALL_FAILURE) {
          setChatMessages(prev => [
            ...prev.slice(0, -1),
            {
              id: uuidv4(),
              author: "ai",
              content: "",
              executionStatus: ExecutionStatusType.TOOL_CALL_FAILURE,
            },
          ]);
          return;
        }

        if (!wasStreamed) {
          // Non-streaming path
          const newArtifacts: ArtifactProto[] = [];
          if (response && response.messages) {
            const lastHumanMsgIdx = response.messages.reduce((lastIdx, msg, idx) => {
              if (chatMessageTypeFromJSON(msg.message_type) === ChatMessageType.HUMAN && msg.content === msgToSend) {
                return idx;
              }
              return lastIdx;
            }, -1);

            const reasoningMessages = response.messages.filter(
              (msg, idx) =>
                idx > lastHumanMsgIdx &&
                chatMessageTypeFromJSON(msg.message_type) === ChatMessageType.REASONING &&
                (msg as ChatMessageProto).execution_steps.length > 0
            );
            const messagesToProcess =
              reasoningMessages.length > 0 ? [reasoningMessages[reasoningMessages.length - 1]] : reasoningMessages;

            for (const msg of messagesToProcess) {
              for (const artifact of (msg as ChatMessageProto).execution_steps.flatMap(s => s.artifacts)) {
                newArtifacts.push(artifact);
              }
            }

            // Also collect artifacts from the human message's execution steps
            // (e.g. CREATING_FILE_ARTIFACT steps attached to the user turn).
            if (lastHumanMsgIdx >= 0) {
              const humanMsg = response.messages[lastHumanMsgIdx] as ChatMessageProto;
              for (const artifact of (humanMsg.execution_steps ?? []).flatMap(s => s.artifacts)) {
                newArtifacts.push(artifact);
              }
            }
          }

          if (newArtifacts.length > 0 && registerArtifacts) {
            registerArtifacts(...newArtifacts);
          }

          setChatMessages(prev => {
            const updated = prev.slice(0, -1);
            if (!response || !response.messages) return updated;

            const userMsgIdx = response.messages.reduce((lastIdx, msg, idx) => {
              if (chatMessageTypeFromJSON(msg.message_type) === ChatMessageType.HUMAN && msg.content === msgToSend) {
                return idx;
              }
              return lastIdx;
            }, -1);
            const thisTurnMessages = userMsgIdx >= 0 ? response.messages.slice(userMsgIdx + 1) : response.messages;

            let responseMsg: ChatMessageProto | undefined;
            let reasoningMsg: ChatMessageProto | undefined;
            for (let i = thisTurnMessages.length - 1; i >= 0; i--) {
              const msg = thisTurnMessages[i] as ChatMessageProto;
              const msgType = chatMessageTypeFromJSON(msg.message_type);
              if (msgType === ChatMessageType.RESPONSE && !responseMsg) {
                responseMsg = msg;
              } else if (msgType === ChatMessageType.REASONING && !reasoningMsg) {
                reasoningMsg = msg;
                if (responseMsg) break;
              } else if (msgType === ChatMessageType.HUMAN) {
                break;
              }
            }

            // The human message is at response.messages[userMsgIdx] — NOT in thisTurnMessages
            // (which starts at userMsgIdx + 1).
            const humanMsg = userMsgIdx >= 0 ? (response.messages[userMsgIdx] as ChatMessageProto) : undefined;
            const humanFileArtifacts = (humanMsg?.execution_steps ?? [])
              .filter(s => s.status === ExecutionStatusType.CREATING_FILE_ARTIFACT)
              .flatMap(s => s.artifacts ?? []);
            if (humanMsg?.timestamp_ms || humanFileArtifacts.length > 0) {
              for (let k = updated.length - 1; k >= 0; k--) {
                if (updated[k].author === "user") {
                  updated[k] = {
                    ...updated[k],
                    ...(humanMsg?.timestamp_ms ? { userTimestampMs: Number(humanMsg.timestamp_ms) } : {}),
                    ...(humanFileArtifacts.length > 0 ? { fileArtifacts: humanFileArtifacts } : {}),
                  };
                  break;
                }
              }
            }

            if (!responseMsg) return updated;

            // Merge human-message steps (e.g. CREATING_FILE_ARTIFACT) before reasoning steps
            // so the full timeline is available on non-streaming reload.
            const humanSteps = humanMsg?.execution_steps ?? [];
            const steps = [...humanSteps, ...(reasoningMsg?.execution_steps ?? [])];
            const artifactsForMessage = steps.flatMap(s => s.artifacts);
            // prevMessageEndTimestampMs: end time of the message just before this
            // AI turn — used to compute the first reasoning block's duration.
            // Rule: if the human message has execution steps, use last step's
            // timestamp; otherwise use the human message's own timestamp.
            let prevMessageEndTimestampMs: number | undefined;
            if (humanMsg) {
              const lastHumanStep = humanSteps[humanSteps.length - 1];
              prevMessageEndTimestampMs = lastHumanStep?.timestamp_ms
                ? Number(lastHumanStep.timestamp_ms)
                : humanMsg.timestamp_ms
                  ? Number(humanMsg.timestamp_ms)
                  : undefined;
            }

            if (responseMsg.content && responseMsg.content.length > 0) {
              updated.push({
                id: uuidv4(),
                author: "ai",
                content: cleanContent ? cleanContent(responseMsg.content) : responseMsg.content,
                ls_trace_url: responseMsg.ls_trace_url,
                executionSteps: steps.length > 0 ? steps : undefined,
                prevMessageEndTimestampMs,
                artifacts: artifactsForMessage.length > 0 ? artifactsForMessage : undefined,
                langgraph_message_json_list: reasoningMsg?.langgraph_message_json_list?.length
                  ? reasoningMsg.langgraph_message_json_list
                  : [],
              });
            } else if (responseMsg.response_message) {
              updated.push({
                id: uuidv4(),
                author: "ai",
                content: "",
                responseMessage: responseMsg.response_message,
                ls_trace_url: responseMsg.ls_trace_url,
                executionSteps: steps.length > 0 ? steps : undefined,
                prevMessageEndTimestampMs,
                artifacts: artifactsForMessage.length > 0 ? artifactsForMessage : undefined,
                langgraph_message_json_list: reasoningMsg?.langgraph_message_json_list?.length
                  ? reasoningMsg.langgraph_message_json_list
                  : [],
              });
            }
            return updated;
          });
        } else {
          // Streaming path: apply cleanContent on final text
          setChatMessages(prev => {
            const updated = [...prev];
            const lastIdx = updated.length - 1;
            if (updated[lastIdx]?.author === "ai" && cleanContent && updated[lastIdx].content) {
              updated[lastIdx] = {
                ...updated[lastIdx],
                content: cleanContent(updated[lastIdx].content),
              };
            }
            return updated;
          });
        }
      } catch (error) {
        setChatMessages(prev => prev.slice(0, -1));
        console.error("Error sending message:", error);
      } finally {
        setIsLoading(false);
        setStreamingStatusLabel("Thinking ...");
      }
    },
    [
      chatId,
      userId,
      isFromEva,
      isChatSaved,
      cleanContent,
      navigate,
      setChatId,
      setChatMessages,
      registerArtifacts,
      setScenarioJobs,
      setCurrentSessionHeader,
      setIsLoading,
      setStreamingStatusLabel,
      prepareForNewTurn,
      appendStreamingToken,
      applyStreamChunk,
      finalizeStreamingTurn,
      handleExecutionStep,
      handleReasoningToken,
    ]
  );

  // Auto-send initialPrompt when set (e.g. from workflow Run/Edit/Create buttons in Eva).
  // When initialAnalystId is provided, wait until selectedAnalyst.id matches it so we
  // never fire with the default analyst while the correct one is still being resolved.
  const isAutoSendPending = useAutoSend(
    initialPrompt,
    chatId === undefined &&
      !isLoading &&
      !!selectedAnalyst &&
      (!initialAnalystId || selectedAnalyst.id === initialAnalystId),
    useCallback(
      prompt => handleSendMessage(prompt, true, selectedAnalyst ?? null),
      [handleSendMessage, selectedAnalyst]
    )
  );

  // Auto-send first message after NewChat navigates here.
  // NewChat persists the session, then navigates to /chat/:sessionId with
  // { isNewSession, pendingMessage, sessionId } in location.state.
  // This hook picks up that pending message and sends it as the first turn.
  const navState = location.state as {
    isNewSession?: boolean;
    pendingMessage?: string;
    sessionId?: string;
    pendingCsvFiles?: { file: File; fileName: string }[];
  } | null;
  const pendingCsvFilesFromNav =
    navState?.isNewSession && chatId === navState?.sessionId ? navState.pendingCsvFiles : undefined;
  useAutoSend(
    navState?.isNewSession && chatId === navState?.sessionId ? navState?.pendingMessage : undefined,
    !!chatId && !isLoading,
    useCallback(
      msg => handleSendMessage(msg, false, selectedAnalyst ?? null, pendingCsvFilesFromNav),
      [handleSendMessage, selectedAnalyst, pendingCsvFilesFromNav]
    )
  );

  // When a user sends a file-only message from NewChatPage (no text), NewChatPage creates the
  // session and navigates here with isNewSession=true and pendingCsvFiles but an empty
  // pendingMessage. The normal useAutoSend hook skips empty prompts, so the files would never
  // be streamed to Darwin. This effect detects that specific case — new session, files present,
  // no text — and fires handleSendMessage once to stream the files through the continue path.
  const hasFiredFileOnlyAutoSendRef = useRef(false);
  useEffect(() => {
    const isNewSession = navState?.isNewSession && chatId === navState?.sessionId;
    const hasFiles = pendingCsvFilesFromNav && pendingCsvFilesFromNav.length > 0;
    const hasPendingText = !!navState?.pendingMessage?.trim();
    if (!isNewSession || !hasFiles || hasPendingText || !chatId || isLoading || hasFiredFileOnlyAutoSendRef.current)
      return;
    hasFiredFileOnlyAutoSendRef.current = true;
    handleSendMessage("", false, selectedAnalyst ?? null, pendingCsvFilesFromNav);
  }, [chatId, isLoading, navState, pendingCsvFilesFromNav, handleSendMessage, selectedAnalyst]);

  // Derive saved scenario jobs from last message after streaming ends
  useEffect(() => {
    if (isLoading || chatMessages.length === 0 || !setScenarioJobsSavedByTools) return;
    const lastMessage = chatMessages[chatMessages.length - 1];
    if (!lastMessage) return;
    const extracted = extractScenarioJobsSavedByTools([lastMessage]);
    setScenarioJobsSavedByTools(prev => {
      if (prev.length === extracted.length && prev.every((job, i) => job === extracted[i])) {
        return prev;
      }
      return extracted;
    });
  }, [chatMessages, isLoading, setScenarioJobsSavedByTools]);

  return (
    <ChatPanel
      isLoadingChat={props.isLoadingChat}
      chatId={props.chatId}
      chatMessages={props.chatMessages}
      sessionId={props.sessionId}
      onArtifactClick={props.onArtifactClick}
      selectedAnalyst={props.selectedAnalyst}
      analystsList={props.analystsList}
      isLoadingAnalysts={props.isLoadingAnalysts}
      isFromEva={props.isFromEva}
      onAnalystChange={props.onAnalystChange}
      isArtifactsPanelVisible={props.isArtifactsPanelVisible}
      lookupByName={props.lookupByName}
      artifactMap={props.artifactMap}
      scenarioJobs={props.scenarioJobs}
      isLoading={isLoading}
      streamingStatusLabel={streamingStatusLabel}
      onSendMessage={handleSendMessage}
      isAutoSendPending={isAutoSendPending}
      currentSessionHeader={currentSessionHeader}
    />
  );
};

export default ChatContainer;
